import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  alpha,
  useTheme,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
} from "@mui/material";
import {
  Edit,
  DeleteOutline,
  Group,
  PhotoCamera,
  FamilyRestroom,
  TimerOutlined,
  AutoAwesome,
  PriorityHigh,
  AssignmentOutlined,
  Add as AddIcon,
} from "@mui/icons-material";

import { QK } from "../../../../../base/qk";
import {
  deleteVisitByID,
  getVisitByClientID,
} from "../../../../../api/services/scheduling";
import CreateDirectVisitModal from "./CreateDirectVisitModal";
import EditScheduleModal from "../../../../../screens/VisitPlanScreen/VisitPlanModal/EditScheduleModal";
import ConfirmModal from "../../../../ConfirmModal/ConfirmModal";

const DirectVisit = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rowID, setRowID] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [area, setArea] = useState(null);

  const { data: visits } = useQuery([QK.visits, clientID, 3], () =>
    getVisitByClientID(clientID, 3)
  );

  const deleteMutation = useMutation((visitID) => deleteVisitByID(visitID), {
    onSuccess: () => queryClient.invalidateQueries([QK.visits, clientID]),
  });

  const formatTotalMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const handleDelete = (id) => {
    setRowID(id);
    setShowConfirmModal(true);
  };

  const openUpdateModal = (visitID, area) => {
    setSelectedID(visitID);
    setArea(area);
    setShowUpdateModal(true);
  };

  const handleConfirm = () => {
    deleteMutation.mutate(rowID);
    setShowConfirmModal(false);
  };

  const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{mb:4}}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <AutoAwesome
              sx={{ color: theme.palette.primary.main, fontSize: 22 }}
            />
            <Typography
              variant="h6"
              fontWeight={900}
              sx={{ color: "#1e293b", letterSpacing: -0.5 }}
            >
              Direct Visit Plans
            </Typography>
          </Stack>
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            Live scheduled care and specific support instructions
          </Typography>
        </Box>

        {/* ✨ Refined button — icon + label, gradient, hover lift */}
        <Button
          onClick={() => setShowCreateModal(true)}
          variant="contained"
          disableElevation
          startIcon={<AddIcon sx={{ fontSize: 20 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.85rem",
            letterSpacing: 0.2,
            borderRadius: "10px",
            px: 2.5,
            py: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.45)}`,
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            },
          }}
        >
          Create Visit
        </Button>
      </Stack>

      <Grid container spacing={2.5}>
        {visits?.map((visit) => (
          <Grid item xs={12} sm={6} md={4} key={visit.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 4,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: "#fff",
                position: "relative",
                overflow: "hidden",
                transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  boxShadow: `0 12px 24px ${alpha(
                    theme.palette.common.black,
                    0.05
                  )}`,
                },
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <Box sx={{ flexGrow: 1, pr: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight={900}
                    sx={{ color: "#0f172a", lineHeight: 1.2 }}
                  >
                    {visit.visit_name}
                  </Typography>
                  {visit.time_critical && (
                    <Chip
                      icon={
                        <PriorityHigh sx={{ fontSize: "10px !important" }} />
                      }
                      label="Time Critical"
                      size="small"
                      color="error"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 800,
                        mt: 0.5,
                      }}
                    />
                  )}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => openUpdateModal(visit.id, visit.area)}
                    sx={{
                      color: "info.main",
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                    }}
                  >
                    <Edit sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(visit.id)}
                    sx={{
                      color: "error.main",
                      bgcolor: alpha(theme.palette.error.main, 0.05),
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Stack>

              <Box
                sx={{
                  p: 1.5,
                  mb: 2,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px dashed ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={900}
                  color="primary.main"
                >
                  {visit?.time_required_from?.slice(0, 5)} -{" "}
                  {visit?.time_required_to?.slice(0, 5)}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={900}
                  sx={{
                    bgcolor: "primary.main",
                    color: "#fff",
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                  }}
                >
                  {formatTotalMinutes(visit.total_duration)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight={800}
                  color="text.disabled"
                  display="block"
                  sx={{
                    mb: 0.8,
                    fontSize: "0.6rem",
                    textTransform: "uppercase",
                  }}
                >
                  Scheduled Days
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  {weekDays.map((day) => {
                    const isActive = visit.selected_days_in_week.includes(day);
                    return (
                      <Box
                        key={day}
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          bgcolor: isActive ? "primary.main" : "#f1f5f9",
                          color: isActive ? "#fff" : "#94a3b8",
                          border: isActive ? "none" : "1px solid #e2e8f0",
                        }}
                      >
                        {day[0]}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              <Box
                sx={{
                  bgcolor: "#f8fafc",
                  p: 1.5,
                  borderRadius: 2.5,
                  mb: 2,
                  border: "1px solid #f1f5f9",
                }}
              >
                <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
                  <AssignmentOutlined
                    sx={{ fontSize: 14, color: "text.disabled" }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={800}
                    color="text.disabled"
                    sx={{ fontSize: "0.6rem" }}
                  >
                    INSTRUCTIONS
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.3,
                    fontStyle: visit.support_instructions
                      ? "normal"
                      : "italic",
                  }}
                >
                  {visit.support_instructions ||
                    "No special instructions provided for this visit."}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2, opacity: 0.5 }} />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" spacing={1.5}>
                  <StatusIcon
                    icon={<PhotoCamera />}
                    active={visit.allow_photos}
                    color="#f59e0b"
                    tooltip="Photos Allowed"
                  />
                  <StatusIcon
                    icon={<FamilyRestroom />}
                    active={visit.share_with_family}
                    color="#06b6d4"
                    tooltip="Shared with Family"
                  />
                  <Tooltip title={`Tolerance: ${visit.time_tolerance} mins`}>
                    <TimerOutlined
                      sx={{
                        fontSize: 18,
                        color:
                          visit.time_tolerance > 0
                            ? "primary.main"
                            : "#cbd5e1",
                      }}
                    />
                  </Tooltip>
                </Stack>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    bgcolor: "#f1f5f9",
                    px: 1,
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  <Group sx={{ fontSize: 14, color: "#64748b" }} />
                  <Typography
                    variant="caption"
                    fontWeight={900}
                    color="#64748b"
                  >
                    {visit.carers_required}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <CreateDirectVisitModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        VisitId={selectedID}
        ClientId={clientID}
      />

      <EditScheduleModal
        area={area}
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        clientId={clientID}
        visitId={selectedID}
        modalWidth="lg"
        fullWidth
        maxWidth="lg"
      />

      <ConfirmModal
        show={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        title="Remove Visit"
        body="Are you sure you want to delete this schedule?"
      />
    </Box>
  );
};

const StatusIcon = ({ icon, active, color, tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <Box
      sx={{
        p: 0.6,
        borderRadius: "8px",
        bgcolor: active ? alpha(color, 0.1) : "#f1f5f9",
        color: active ? color : "#cbd5e1",
        display: "flex",
        transition: "0.2s",
      }}
    >
      {React.cloneElement(icon, { sx: { fontSize: 16 } })}
    </Box>
  </Tooltip>
);

export default DirectVisit;